import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import Container from "connectui/component/layout/Container";
import DynamicGridLayout from "connectui/component/layout/DynamicGridLayout";
import Text from "connectui/component/display/Text";
import {endOfDay, startOfDay, subDays} from "date-fns";

import {
  BoardingPassVerificationCard,
  DashboardEmptyStateCard,
  MessageBreakdownCard,
  MessageInsightsCard,
  ResponseTimeCard,
  RevenueCard,
  RevenuePerformanceCard,
  SentimentCard,
  SummaryCard,
} from "ui/component/dashboard/cards";
import DashboardActionBar from "../ui/component/dashboard/DashboardActionBar";
import {getDashboardOverview} from "redux/actions/dashboardActions";
import {useTranslation} from "react-i18next";
import ICONS from "../connectui/icons/iconMap";
import SimpleCard from "connectui/component/display/card/SimpleCard";

const useStyles = (theme) =>
  buildStyles({
    container: {
      width: "100%",
      height: "100vh",
      backgroundColor: `${theme.colors.gray50} !important`,
      boxShadow: theme.shadows.xs,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    innerContainer: {
      padding: theme.spacing(4),
      backgroundColor: `${theme.colors.gray50} !important`,
      height: "100%",
      overflow: "auto",
    },
    header: {
      padding: theme.spacing(4),
      backgroundColor: `${theme.colors.gray50} !important`,
    },
  });

const GRID_BREAKPOINTS = {
  0: 1,
  768: 1,
  1000: 2,
};

const CARD_ROW_BREAKPOINTS = {
  0: 1,
  1000: 3,
};

const DashboardV2 = () => {
  const dispatch = useDispatch();
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const {t} = useTranslation();
  const overviewData = useSelector(
    (state) => state.dashboardReducer.overviewData,
  );
  const revenueData = useSelector(
    (state) => state.dashboardReducer.revenueData,
  );

  const [timeframe, setTimeframe] = useState({
    startDate: new Date(startOfDay(subDays(new Date(), 29))).getTime(),
    endDate: new Date(endOfDay(new Date())).getTime(),
  });

  const [selectedListingGroups, setSelectedListingGroups] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
  };

  useEffect(() => {
    if (revenueData?.display_currency && !selectedCurrency) {
      setSelectedCurrency(revenueData.display_currency);
    }
  }, [revenueData, selectedCurrency]);

  useEffect(() => {
    dispatch(
      getDashboardOverview({
        start_date: timeframe.startDate,
        end_date: timeframe.endDate,
        onSuccess: (response) => {
          console.log("GOT DASHBOARD OVERVIEW:", response);
        },
        onError: () => {
          console.error("Failed to load overview data");
        },
      }),
    );
  }, [dispatch, timeframe.startDate, timeframe.endDate]);

  const renderOverviewCards = () => {
    if (!overviewData?.overview) {
      return [1, 2, 3].map((cardNumber) => (
        <SummaryCard key={`l-${cardNumber}`} />
      ));
    }

    return Object.entries(overviewData.overview).map(([key, data]) => (
      <SummaryCard key={key} cardKey={key} data={data} />
    ));
  };

  const hasListingGroupsSelected =
    selectedListingGroups && selectedListingGroups.length > 0;

  const getHiddenCardsText = () => {
    if (!hasListingGroupsSelected) return "";

    const hiddenCards = [
      t("dashboard.response-time.title"),
      t("dashboard.message-breakdown.title"),
      t("dashboard.messaging-insights.title"),
    ];

    return t("dashboard.hidden-cards-message", {
      cardsList: hiddenCards.join(", "),
    });
  };

  return (
    <Container className={classes.container}>
      <Container className={classes.header}>
        <Text variant="displayXS" weight="semibold" sx={{flex: 1}}>
          {t("dashboard.header.title")}
        </Text>
      </Container>
      <Container className={classes.innerContainer}>
        <Container sx={{marginBottom: theme.spacing(4), width: "100%"}}>
          <DynamicGridLayout spacing={2} breakpoints={CARD_ROW_BREAKPOINTS}>
            {renderOverviewCards()}
          </DynamicGridLayout>
        </Container>
        <DashboardActionBar
          onTimeframeChange={handleTimeframeChange}
          selectedListingGroups={selectedListingGroups}
          onListingGroupsChange={setSelectedListingGroups}
          selectedCurrency={selectedCurrency}
          onCurrencyChange={setSelectedCurrency}
        />
        <DynamicGridLayout spacing={2} breakpoints={GRID_BREAKPOINTS}>
          {!hasListingGroupsSelected && (
            <ResponseTimeCard timeframe={timeframe} />
          )}

          <RevenueCard
            timeframe={timeframe}
            currency={selectedCurrency}
            listingGroups={selectedListingGroups}
          />
          <SentimentCard
            timeframe={timeframe}
            listingGroups={selectedListingGroups}
          />
          <RevenuePerformanceCard
            timeframe={timeframe}
            currency={selectedCurrency}
            listingGroups={selectedListingGroups}
          />

          {/* Only show messaging cards if no listing groups are selected */}
          {!hasListingGroupsSelected && (
            <MessageBreakdownCard
              timeframe={timeframe}
              listingGroups={selectedListingGroups}
            />
          )}

          {!hasListingGroupsSelected && (
            <MessageInsightsCard
              timeframe={timeframe}
              listingGroups={selectedListingGroups}
            />
          )}
          <BoardingPassVerificationCard
            timeframe={timeframe}
            listingGroups={selectedListingGroups}
          />

          {hasListingGroupsSelected && (
            <SimpleCard
              customContent={
                <DashboardEmptyStateCard
                  text={getHiddenCardsText()}
                  icon={ICONS["missing-cards-white"]}
                  height="300px"
                />
              }
              sx={{height: "100%", width: "100%"}}
            />
          )}
        </DynamicGridLayout>
      </Container>
    </Container>
  );
};

export default DashboardV2;
